/**
 * Formateadores compartidos por todas las pantallas del módulo LCL.
 *
 * En el módulo aéreo estos helpers están copiados literalmente dentro
 * de cada board page. Aquí viven una sola vez y los 4 tableros los
 * importan: el LCL sigue siendo código propio (no se comparte nada
 * con el aéreo), pero sin la duplicación.
 */

/**
 * Factores de conversión de cada unidad de dimensión a CENTÍMETROS.
 * DEBEN coincidir con `FACTOR_A_CM` de `lcl-requests.service.ts`.
 */
export const FACTOR_A_CM = {
  cm: 1,
  m: 100,
  in: 2.54,
};
export const KG_POR_LIBRA = 0.45359237;
export const CM3_POR_M3 = 1_000_000;
/** Mínimo facturable estándar en LCL: 1 unidad W/M. */
export const MINIMO_WM = 1;

export const shortId = (id) => (id ? String(id).slice(-6) : '—');

export const formatCodigo = (r) => r?.codigo || shortId(r?._id);

export const formatCliente = (r) => r?.clienteSnapshot?.nombre || shortId(r?.clienteId);

export const formatClienteNit = (r) => r?.clienteSnapshot?.nit || '';

/**
 * Ruta principal: ciudad + UN/LOCODE de cada puerto.
 * Ej. "Buenaventura (COBUN) → Shanghái (CNSHA)".
 */
export const formatRuta = (r) => {
  const a = r?.puertoCargaSnapshot;
  const b = r?.puertoDescargaSnapshot;
  const leftCity = a?.ciudad || a?.nombre;
  const rightCity = b?.ciudad || b?.nombre;
  const leftCode = a?.unlocode;
  const rightCode = b?.unlocode;
  if (leftCity && rightCity) {
    return `${leftCity}${leftCode ? ` (${leftCode})` : ''} → ${rightCity}${rightCode ? ` (${rightCode})` : ''}`;
  }
  if (leftCode && rightCode) return `${leftCode} → ${rightCode}`;
  if (r?.paisOrigen && r?.paisDestino) return `${r.paisOrigen} → ${r.paisDestino}`;
  return `${shortId(r?.puertoCargaId)} → ${shortId(r?.puertoDescargaId)}`;
};

/** Sub-ruta: países. */
export const formatRutaSub = (r) => {
  const a = r?.puertoCargaSnapshot;
  const b = r?.puertoDescargaSnapshot;
  const leftPais = a?.pais || r?.paisOrigen;
  const rightPais = b?.pais || r?.paisDestino;
  if (leftPais && rightPais) return `${leftPais} → ${rightPais}`;
  return '';
};

export const formatDate = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch {
    return '—';
  }
};

export const formatDateTime = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const round3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;

/** Volumen en m³ con 3 decimales. Ej. "2.775 m³". */
export const formatCbm = (v) =>
  v == null ? '—' : `${round3(v).toLocaleString('es-ES', { maximumFractionDigits: 3 })} m³`;

/** Peso en kg con 2 decimales. */
export const formatPeso = (v) =>
  v == null ? '—' : `${Number(v).toFixed(2)} kg`;

/** Unidades facturables W/M con 3 decimales. */
export const formatWM = (v) =>
  v == null ? '—' : `${round3(v).toLocaleString('es-ES', { maximumFractionDigits: 3 })} W/M`;

/**
 * Calcula los totales de carga a partir del detalle de bultos.
 *
 * ESTA FÓRMULA DEBE COINCIDIR con `calcularTotalesLcl()` de
 * `logistic-back/src/lcl-requests/services/lcl-requests.service.ts`.
 * El servidor recalcula y rechaza la solicitud si el volumen difiere
 * más de un 1%, así que cualquier cambio hay que hacerlo en ambos.
 *
 *   volumenLinea m³ = (L × A × H, en cm) × piezas / 1.000.000
 *   pesoLinea kg    = pesoUnitario × piezas
 *   toneladas       = pesoTotal / 1000
 *   unidadesWM      = max(1, volumenTotal, toneladasTotal)
 */
export const calcularTotalesLcl = (detalles = []) => {
  let numPiezas = 0;
  let pesoTotal = 0;
  let volumenTotal = 0;
  const volumenPorLinea = [];

  for (const d of detalles) {
    const piezas = Number(d?.numPiezas) || 0;
    const factorDim = FACTOR_A_CM[d?.unidadDimension] ?? 1;
    const factorPeso = d?.unidadPeso === 'lb' ? KG_POR_LIBRA : 1;

    const largoCm = (Number(d?.largo) || 0) * factorDim;
    const anchoCm = (Number(d?.ancho) || 0) * factorDim;
    const altoCm = (Number(d?.alto) || 0) * factorDim;

    const volumenLinea = (largoCm * anchoCm * altoCm * piezas) / CM3_POR_M3;
    const pesoLinea = (Number(d?.peso) || 0) * factorPeso * piezas;

    numPiezas += piezas;
    pesoTotal += pesoLinea;
    volumenTotal += volumenLinea;
    volumenPorLinea.push(round3(volumenLinea));
  }

  pesoTotal = round3(pesoTotal);
  volumenTotal = round3(volumenTotal);
  const toneladasTotal = round3(pesoTotal / 1000);
  const unidadesWM = round3(Math.max(MINIMO_WM, volumenTotal, toneladasTotal));

  return { numPiezas, pesoTotal, volumenTotal, toneladasTotal, unidadesWM, volumenPorLinea };
};
